import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  Tooltip,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon, CheckIcon, COLORS } from "../Token";
import { ApprovalStatus } from "../Types";
import { inductionService } from "../services/inductionService";
// import InductionBackground from "../../../assets/images/completion-induction.png";

import SuccessAnimation from "../../../../assets/lottie/Success.json";
import { BellIcon, LockIcon, SettingsIcon, RepeatIcon, CheckCircleIcon } from "@chakra-ui/icons";
import MyDiv from "./InductionExperience.style"
import Lottie from "lottie-react";

interface CompletionScreenProps {
  employeeName: string;
  companyName: string;
  branchName: string;
  businessName?: string;
}

export function CompletionScreen({
  employeeName,
  companyName,
  branchName,
  businessName,
}: CompletionScreenProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [approval, setApproval] = useState<ApprovalStatus | null>(null);

  const fetchStatus = useCallback(() => {
    setLoading(true);
    inductionService
      .getApprovalStatus()
      .then((res) => setApproval(res))
      .catch(() => setApproval(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchStatus();
    // Poll every 15 seconds if approvals are pending so the screen live-unlocks as soon as both sign off
    const interval = setInterval(() => {
      inductionService
        .getApprovalStatus()
        .then((res) => {
          setApproval(res);
        })
        .catch(() => {});
    }, 15000);

    return () => clearInterval(interval);
  }, [fetchStatus]);

  const hrApproved = approval?.hrStatus === "Approved";
  const branchHeadApproved = approval?.branchHeadStatus === "Approved";
  const crmUnlocked = approval?.crmAccessGranted || (hrApproved && branchHeadApproved);
  const crmName = approval?.crmName || (businessName?.includes("Staffing") ? "Teklense" : "Bivocal");
  const crmUrl = approval?.crmUrl || (crmName === "Teklense" ? "https://teklense.com" : "https://bivocal.com");

  return (
    <MyDiv>
    <Flex
      className="induction-completion-scene"
      minH="100dvh"
      w="100%"
      bg={COLORS.forest}
      // backgroundImage={`url(${InductionBackground})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      color="#F2F7F4"
      align="center"
      justify="center"
      direction="column"
      px={{ base: 4, md: 8 }}
      py={{ base: 6, md: 8 }}
      position="relative"
      overflow="hidden"
      boxSizing="border-box"
    >
      <Flex className="completion-main-content" position="relative" direction="column" align="center" gap={6} maxW="900px" w="100%" textAlign="center">
        <Box display="flex" justifyContent="center" alignItems="center" w="100%">
          <Lottie animationData={SuccessAnimation} loop={false} style={{ height: 110, width: 110 }} />
        </Box>

        <Box>
          <Badge
            colorScheme="green"
            borderRadius="full"
            px={4}
            py={1}
            fontSize="sm"
            fontWeight={600}
            bg="#0A4A33"
            color="#BFEFD3"
            mb={2}
          >
            ✓ Induction Modules & Feedback Completed
          </Badge>
          <Heading
            as="h1"
            className="induction-display"
            fontSize={{ base: "30px", md: "42px" }}
            fontWeight={700}
            lineHeight={1.15}
            color="black"
          >
            Congratulations, {employeeName}!
          </Heading>
          <Text fontSize="md" color="#4a5f54" mt={1.5} maxW="580px" mx="auto">
            You have successfully completed your induction at {companyName} ({branchName}).
          </Text>
        </Box>

        {/* Stakeholder Notification Banner */}
        <Box
          w="100%"
          p={{ base: 3, md: 4 }}
          borderRadius="16px"
          bg="rgba(240, 249, 243, 0.92)"
          border="1px solid"
          borderColor="rgba(190, 224, 203, 0.65)"
          boxShadow="0 4px 16px rgba(20, 90, 55, 0.06)"
          backdropFilter="blur(12px)"
          textAlign="left"
        >
          <Flex align="center" gap={3}>
            <Flex
              w="36px"
              h="36px"
              borderRadius="full"
              bg="#D0EEDB"
              color="#087443"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <BellIcon boxSize={5} />
            </Flex>

            <Box flex="1" minW={0}>
              <Text fontSize="sm" fontWeight={700} color="#214B3C" lineHeight={1.4}>
                Stakeholder Notifications Dispatched
              </Text>
              <Text fontSize="xs" color="#64786F" lineHeight={1.5}>
                HR Manager, Branch Head, and Department Head have received notice of your induction completion.
              </Text>
            </Box>

            <Flex
              w="18px"
              h="18px"
              borderRadius="full"
              bg="#087F52"
              color="white"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <CheckIcon boxSize={2.5} />
            </Flex>
          </Flex>
        </Box>

        {/* Approval & Access Status Card */}
        <Box
          w="100%"
          p={{ base: 4, md: 5 }}
          borderRadius="20px"
          bg="rgba(255, 255, 255, 0.95)"
          color={COLORS.ink}
          textAlign="left"
          border="1px solid rgba(255, 255, 255, 0.85)"
          boxShadow="0 12px 35px rgba(29, 65, 45, 0.12)"
          backdropFilter="blur(14px)"
        >
          <Flex align="center" justify="space-between" mb={4} gap={2} flexWrap="wrap">
            <Flex align="center" gap={2.5}>
              <Flex
                w="32px"
                h="32px"
                borderRadius="full"
                bg="#D8F1E2"
                color="#087443"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <SettingsIcon boxSize={4} />
              </Flex>
              <Text fontWeight={700} fontSize="15px" color="#263B33">
                Dual Sign-Off & HRlense Portal Access
              </Text>
            </Flex>
            <Badge
              colorScheme={crmUnlocked ? "green" : "orange"}
              borderRadius="full"
              px={2.5}
              py={0.5}
              fontSize="10px"
              fontWeight={700}
            >
              {crmUnlocked ? "✓ ACCESS UNLOCKED" : "🔒 APPROVAL IN PROGRESS"}
            </Badge>
          </Flex>

          {loading ? (
            <Flex justify="center" py={6}>
              <Spinner color={COLORS.brand} />
            </Flex>
          ) : (
            <VStack spacing={0} align="stretch">
              {/* HR Manager Approval */}
              <Flex align="stretch" gap={3}>
                <Flex w="40px" flexShrink={0} direction="column" align="center">
                  <Flex
                    w="30px"
                    h="30px"
                    borderRadius="full"
                    bg={hrApproved ? "#D5F0E0" : "#BFE8D0"}
                    color="#087443"
                    align="center"
                    justify="center"
                    fontWeight={700}
                    fontSize="13px"
                  >
                    {hrApproved ? <CheckIcon boxSize={3.5} /> : "1"}
                  </Flex>
                  <Box flex="1" minH="30px" borderLeft="2px dotted #8CC9A5" />
                </Flex>

                <Flex
                  flex="1"
                  minW={0}
                  mb={2}
                  p={{ base: 3, md: 3.5 }}
                  borderRadius="14px"
                  bg={hrApproved ? "#F0F9F3" : "#F7F9F8"}
                  border="1px solid"
                  borderColor={hrApproved ? "#D5EBDD" : "#E4ECE7"}
                  align="center"
                  justify="space-between"
                  gap={2}
                >
                  <Box minW={0}>
                    <Text fontWeight={600} fontSize="13px" color="#30463D">
                      1 · HR Manager Approval
                    </Text>
                    <Text fontSize="11px" color="#75827C" mt={0.5}>
                      {hrApproved
                        ? `Approved by ${approval?.hrApprovedBy || "HR Manager"}`
                        : "Awaiting review and sign-off by HR Manager"}
                    </Text>
                  </Box>
                  <Badge
                    flexShrink={0}
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="10px"
                    fontWeight={700}
                    color={hrApproved ? "#087443" : "#A85A0A"}
                    bg={hrApproved ? "#D8F2E2" : "#FCEBCB"}
                  >
                    {approval?.hrStatus || "PENDING"}
                  </Badge>
                </Flex>
              </Flex>

              {/* Branch Head Approval */}
              <Flex align="stretch" gap={3}>
                <Flex w="40px" flexShrink={0} direction="column" align="center">
                  <Flex
                    w="30px"
                    h="30px"
                    borderRadius="full"
                    bg={branchHeadApproved ? "#D5F0E0" : "#BFE8D0"}
                    color="#087443"
                    align="center"
                    justify="center"
                    fontWeight={700}
                    fontSize="13px"
                  >
                    {branchHeadApproved ? <CheckIcon boxSize={3.5} /> : "2"}
                  </Flex>
                  <Box flex="1" minH="30px" borderLeft="2px dotted #8CC9A5" />
                </Flex>

                <Flex
                  flex="1"
                  minW={0}
                  mb={2}
                  p={{ base: 3, md: 3.5 }}
                  borderRadius="14px"
                  bg={branchHeadApproved ? "#F0F9F3" : "#F7F9F8"}
                  border="1px solid"
                  borderColor={branchHeadApproved ? "#D5EBDD" : "#E4ECE7"}
                  align="center"
                  justify="space-between"
                  gap={2}
                >
                  <Box minW={0}>
                    <Text fontWeight={600} fontSize="13px" color="#30463D">
                      2 · Branch Head Approval
                    </Text>
                    <Text fontSize="11px" color="#75827C" mt={0.5}>
                      {branchHeadApproved
                        ? `Approved by ${approval?.branchHeadApprovedBy || "Branch Head"}`
                        : "Awaiting final clearance and sign-off by Branch Head"}
                    </Text>
                  </Box>
                  <Badge
                    flexShrink={0}
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="10px"
                    fontWeight={700}
                    color={branchHeadApproved ? "#087443" : "#A85A0A"}
                    bg={branchHeadApproved ? "#D8F2E2" : "#FCEBCB"}
                  >
                    {approval?.branchHeadStatus || "PENDING"}
                  </Badge>
                </Flex>
              </Flex>

              {/* CRM & Portal Activation */}
              <Flex align="center" gap={3}>
                <Flex w="40px" flexShrink={0} justify="center">
                  <Flex
                    w="30px"
                    h="30px"
                    borderRadius="full"
                    bg={crmUnlocked ? "#D8F2E2" : "#FEE2E2"}
                    color={crmUnlocked ? "#087443" : "#DC2626"}
                    align="center"
                    justify="center"
                  >
                    {crmUnlocked ? <CheckIcon boxSize={4} /> : <LockIcon boxSize={3.5} />}
                  </Flex>
                </Flex>

                <Flex
                  flex="1"
                  minW={0}
                  p={{ base: 3, md: 3.5 }}
                  borderRadius="14px"
                  bg={crmUnlocked ? "#EFF9F2" : "#FEF2F2"}
                  border="1px solid"
                  borderColor={crmUnlocked ? "#D5EBDD" : "#FECACA"}
                  align="center"
                  justify="space-between"
                  gap={3}
                  flexWrap="wrap"
                >
                  <Box flex="1" minW="150px">
                    <Text fontWeight={600} fontSize="13px" color="#30463D">
                      3 · HRlense Portal & CRM Provisioning
                    </Text>
                    <Text fontSize="11px" color="#75827C" mt={0.5}>
                      {crmUnlocked
                        ? "Both sign-offs completed! Your HRlense portal access and CRM account are active."
                        : "HRlense portal and CRM access unlock automatically once both HR and Branch Head sign off."}
                    </Text>
                  </Box>

                  {crmUnlocked && (
                    <Button
                      as="a"
                      href={crmUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      h="34px"
                      px={3}
                      borderRadius="9px"
                      bg="#087443"
                      color="white"
                      fontSize="xs"
                      flexShrink={0}
                      rightIcon={<ArrowRightIcon boxSize={3} />}
                      _hover={{ bg: "#065B35" }}
                    >
                      Launch {crmName}
                    </Button>
                  )}
                </Flex>
              </Flex>
            </VStack>
          )}
        </Box>

        {/* Lock Alert Notice when Pending Approval */}
        {!crmUnlocked && (
          <Box
            w="100%"
            p={3.5}
            borderRadius="14px"
            bg="rgba(254, 242, 242, 0.96)"
            border="1px solid #FECACA"
            boxShadow="0 4px 12px rgba(220, 38, 38, 0.08)"
            textAlign="left"
          >
            <Flex align="center" gap={3}>
              <Box
                w="32px"
                h="32px"
                borderRadius="full"
                bg="#FEE2E2"
                color="#DC2626"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <LockIcon boxSize={4} />
              </Box>
              <Box>
                <Text fontWeight="700" fontSize="13px" color="#991B1B">
                  HRlense Portal Access Locked
                </Text>
                <Text fontSize="11.5px" color="#7F1D1D" mt={0.5} lineHeight={1.4}>
                  Your feedback is saved. As per company policy, you cannot open HRlense portal until both HR and Branch Head approve your onboarding.
                </Text>
              </Box>
            </Flex>
          </Box>
        )}

        {/* Action Buttons: Locked until Both Approvals Done */}
        <Flex gap={4} wrap="wrap" justify="center" align="center" pt={1}>
          <Button
            onClick={fetchStatus}
            isLoading={loading}
            leftIcon={<RepeatIcon />}
            variant="outline"
            colorScheme="whiteAlpha"
            color="white"
            borderColor="whiteAlpha.400"
            _hover={{ bg: "whiteAlpha.200" }}
            size="md"
            borderRadius="12px"
          >
            Check Live Approval Status
          </Button>

          {crmUnlocked ? (
            <Button
              onClick={() => navigate("/")}
              h="52px"
              px={8}
              borderRadius="14px"
              bg={COLORS.mint}
              color={COLORS.forest}
              fontSize="md"
              fontWeight={700}
              _hover={{ bg: "#9AF0BC", transform: "translateY(-1px)" }}
              rightIcon={<ArrowRightIcon boxSize={4} />}
              boxShadow="0 6px 18px rgba(10, 74, 51, 0.28)"
            >
              Enter HRlense Portal
            </Button>
          ) : (
            <Tooltip
              label="Access to HRlense remains locked until both HR Manager and Branch Head complete approval"
              hasArrow
            >
              <Button
                isDisabled
                h="52px"
                px={8}
                borderRadius="14px"
                bg="rgba(255, 255, 255, 0.2)"
                color="rgba(255, 255, 255, 0.6)"
                fontSize="md"
                fontWeight={600}
                leftIcon={<LockIcon />}
                cursor="not-allowed"
                _disabled={{ bg: "rgba(255, 255, 255, 0.2)", color: "rgba(255, 255, 255, 0.5)", cursor: "not-allowed" }}
              >
                Go to Dashboard (Locked)
              </Button>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </Flex>
    </MyDiv>
  );
}
